import { inject, Injectable } from '@angular/core';
import { catchError, map, take, tap } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { StationsQuery } from './stations.query';
import { StationsState, StationsStore } from './stations.store';
import {
  AdminStationSearchParamModel,
  ChangeEntryModel,
  ChangesService,
  LoadpointModel,
  LoadpointService,
  OperatorInfoModel,
  StationModel,
  StationService,
} from '@railmybox/api-booking';
import { OperatorModel, OperatorService, StationOperatorModel, StationOperatorService } from '@railmybox/api-dispo';
import { arrayAdd, arrayUpdate } from '@datorama/akita';
import { SelectItem, SortDirectionChangeEvent } from '@eg/ui';
import {
  StationStorageModel,
  StationSurchargeModel,
  StationSurchargeTypeModel,
  StorageService,
  SurchargeService,
} from '@railmybox/api-pricing';
import { arrayNaturalSort, SortDirection } from '@railmybox/shared/util';

@Injectable({ providedIn: 'root' })
export class StationsStoreService {
  private store = inject(StationsStore);
  private query = inject(StationsQuery);
  private stationsService = inject(StationService);
  private loadpointService = inject(LoadpointService);
  private storageService = inject(StorageService);
  private stationOperatorService = inject(StationOperatorService);
  private operatorService = inject(OperatorService);
  private changesService = inject(ChangesService);
  private surchargeService = inject(SurchargeService);

  filter(filters?: AdminStationSearchParamModel, limit?: number, offset?: number) {
    this.store.setError(null);
    return this.stationsService.adminListStations(filters, limit, offset).pipe(
      tap((entities: StationModel[]) => this.store.set(entities)),
      catchError((err) => {
        this.store.setError(err);
        this.store.setLoading(false);
        return throwError(err);
      })
    );
  }

  async getStationsList(): Promise<void> {
    await this.filter();
  }

  getAllStations(active = false): Observable<StationModel[]> {
    return this.stationsService.adminListStations({ active });
  }

  getQuery(): StationsQuery {
    return this.query;
  }

  sortSurcharges(event: SortDirectionChangeEvent<StationSurchargeModel>) {
    let sortedList: StationSurchargeModel[] = [];
    const list = this.query.getSurcharges();

    if (event.valueGetterFn === undefined) {
      sortedList = arrayNaturalSort(
        list as StationSurchargeModel[],
        event.key as keyof StationSurchargeModel,
        event.sortDirection
      ) as StationSurchargeModel[];
    } else {
      const options: Intl.CollatorOptions = {
        numeric: true,
        sensitivity: 'base',
      };

      sortedList = list.sort((a, b) => {
        return event.sortDirection === SortDirection.ASC
          ? event.valueGetterFn(a).toString().localeCompare(event.valueGetterFn(b), undefined, options)
          : event.valueGetterFn(b).toString().localeCompare(event.valueGetterFn(a), undefined, options);
      });
    }

    this.store.update((state) => {
      return {
        ...state,
        additionalSurcharges: sortedList,
      };
    });
  }

  fetchOperatorCodes(stationId: string): Promise<StationOperatorModel[]> {
    return this.stationOperatorService
      .adminListStationOperators(stationId)
      .pipe(
        tap((operatorCodes: StationOperatorModel[]) => {
          this.store.update((state) => {
            return {
              ...state,
              operatorCodes: operatorCodes,
            };
          });
        })
      )
      .toPromise();
  }

  fetchLoadpoints(stationId: string): Promise<LoadpointModel[]> {
    return this.loadpointService
      .adminListLoadpoints(stationId)
      .pipe(
        tap((loadpoints: LoadpointModel[]) => {
          this.store.update((state) => {
            return {
              ...state,
              loadpoints: loadpoints,
            };
          });
        })
      )
      .toPromise();
  }

  fetchFees(stationId: string): Promise<StationStorageModel[]> {
    return this.storageService
      .getAdminStationStorage(stationId)
      .pipe(
        tap((fees: StationStorageModel[]) => {
          this.store.update((state) => {
            return {
              ...state,
              fees: fees,
            };
          });
        })
      )
      .toPromise();
  }

  createFee(stationId: string, fee: StationStorageModel): Observable<StationStorageModel> {
    return this.storageService.createAdminStationStorage(stationId, fee).pipe(
      tap((fee) => {
        this.store.update((entity) => {
          const fees = entity.fees;

          return {
            ...entity,
            fees: arrayAdd(fees, fee),
          };
        });
      })
    );
  }

  createLoadpoint(stationId: string, loadpoint: LoadpointModel): Observable<LoadpointModel> {
    return this.loadpointService.adminCreateLoadpoint(stationId, loadpoint).pipe(
      tap((loadpoint) => {
        this.store.update((entity) => {
          const loadpoints = entity.loadpoints;

          return {
            ...entity,
            loadpoints: arrayAdd(loadpoints, loadpoint),
          };
        });
      })
    );
  }

  updateLoadpoint(stationId: string, loadpointId: string, loadpoint: LoadpointModel): Observable<LoadpointModel> {
    return this.loadpointService.adminUpdateLoadpoint(stationId, loadpointId, loadpoint).pipe(
      tap((loadpoint) => {
        this.store.update((entity) => {
          const loadpoints = entity.loadpoints;

          return {
            ...entity,
            loadpoints: arrayUpdate(loadpoints, loadpoint.id, loadpoint),
          };
        });
      })
    );
  }

  fetchAdditionalSurcharges(stationId: string): Promise<StationSurchargeModel[]> {
    return this.surchargeService
      .adminListSurcharges(StationSurchargeTypeModel.Station, stationId)
      .pipe(
        tap((additionalSurcharges: StationSurchargeModel[]) => {
          this.store.update((state) => {
            return {
              ...state,
              additionalSurcharges: additionalSurcharges,
            };
          });
        })
      )
      .toPromise();
  }

  createAdditionalSurcharge(stationId: string, additionalSurcharge: StationSurchargeModel): Observable<StationSurchargeModel> {
    return this.surchargeService.adminCreateSurcharge(StationSurchargeTypeModel.Station, stationId, additionalSurcharge).pipe(
      tap((additionalSurcharge) => {
        this.fetchAdditionalSurcharges(additionalSurcharge.stationId);
      })
    );
  }

  updateAdditionalSurcharge(
    stationId: string,
    surchargeId: string,
    additionalSurcharge: StationSurchargeModel
  ): Observable<StationSurchargeModel> {
    return this.surchargeService.adminUpdateSurcharge(StationSurchargeTypeModel.Station, stationId, surchargeId, additionalSurcharge).pipe(
      tap((additionalSurcharge) => {
        this.fetchAdditionalSurcharges(additionalSurcharge.stationId);
      })
    );
  }

  createOperatorCode(stationId: string, operatorCode: StationOperatorModel): Observable<StationOperatorModel> {
    return this.stationOperatorService.adminCreateStationOperator(stationId, operatorCode).pipe(
      tap((operatorCode) => {
        this.store.update((entity) => {
          const operatorCodes = entity.operatorCodes;

          return {
            ...entity,
            operatorCodes: arrayAdd(operatorCodes, operatorCode),
          };
        });
      })
    );
  }

  updateOperatorCode(stationId: string, operatorCodeId: string, operatorCode: StationOperatorModel): Observable<StationOperatorModel> {
    return this.stationOperatorService.adminUpdateStationOperator(stationId, operatorCodeId, operatorCode).pipe(
      tap((operatorCode) => {
        this.store.update((entity) => {
          const operatorCodes = entity.operatorCodes;

          return {
            ...entity,
            operatorCodes: arrayUpdate(operatorCodes, operatorCode.id, operatorCode),
          };
        });
      })
    );
  }

  onSaveOrUpdateOperatorCode(
    stationId: string,
    operatorCodeId: string,
    operatorCode: StationOperatorModel
  ): Observable<StationOperatorModel> {
    if (operatorCodeId) {
      return this.updateOperatorCode(stationId, operatorCodeId, operatorCode);
    } else {
      return this.createOperatorCode(stationId, operatorCode);
    }
  }

  deleteOperatorCode(stationId: string, operatorCodeId: string): Observable<StationOperatorModel> {
    return this.stationOperatorService.adminDeleteStationOperator(stationId, operatorCodeId).pipe(
      tap(() => {
        this.store.update((entity) => {
          const operatorCodes = entity.operatorCodes;

          return {
            ...entity,
            operatorCodes: operatorCodes.filter((operatorCode) => operatorCode.id !== operatorCodeId),
          };
        });
      })
    );
  }

  updateFee(stationId: string, feeId: string, fee: StationStorageModel): Observable<StationStorageModel> {
    return this.storageService.updateAdminStationStorage(stationId, feeId, fee).pipe(
      tap((fee) => {
        this.store.update((entity) => {
          const fees = entity.fees;

          return {
            ...entity,
            fees: arrayUpdate(fees, fee.id, fee),
          };
        });
      })
    );
  }

  deleteFee(stationId: string, feeId: string) {
    return this.storageService.deleteAdminStationStorage(stationId, feeId).pipe(
      tap(() => {
        this.store.remove(feeId);
      })
    );
  }

  updateStation(id: string, model: StationModel): Observable<StationModel> {
    if (id) {
      return this.stationsService.adminUpdateStation(id, model).pipe(tap((saved) => this.store.upsert(saved.id, saved)));
    } else {
      return this.stationsService.adminCreateStation(model).pipe(tap((saved) => this.store.upsert(saved.id, saved)));
    }
  }

  updateUiState(uiState: StationsState['ui']): void {
    this.store.update({ ui: uiState });
  }

  reset() {
    this.store.reset();
  }

  updateFilter(filters: AdminStationSearchParamModel) {
    const empty = Object.values(filters).every((value) => value == null);
    if (!empty) {
      this.store.setLoading(true);
    }
    this.store.update({ filters, offset: 0 });
  }

  next() {
    this.store.setLoading(true);
    this.store.update((state) => ({ ...state, offset: state.offset + 1 }));
  }

  previous() {
    this.store.setLoading(true);
    this.store.update((state) => ({ ...state, offset: Math.max(0, state.offset - 1) }));
  }

  fetchAllLoadpoints(): void {
    this.loadpointService.listLoadpoints().subscribe((loadpoints) => {
      this.store.update((state) => {
        return {
          ...state,
          loadpoints: loadpoints,
        };
      });
    });
  }

  getLoadPointsSelectItems(): Observable<SelectItem[]> {
    return this.getQuery()
      .getLoadpoints()
      .pipe(
        map((loadpoints) => {
          return loadpoints.map((el) => ({ label: el.name, value: el.displayCode }) as SelectItem);
        })
      );
  }

  getLoadPointsIdSelectItems(): Observable<SelectItem[]> {
    return this.getQuery()
      .getLoadpoints()
      .pipe(
        map((loadpoints) => {
          return loadpoints.map((el) => ({ label: el.name, value: el.id }) as SelectItem);
        })
      );
  }

  getLoadPoints(stationId: string): Observable<SelectItem[]> {
    return this.getQuery()
      .getLoadpoints()
      .pipe(
        map((loadpoints) =>
          loadpoints
            .filter((el) => el.active && el.stationId === stationId)
            .map(
              (el) =>
                ({
                  label: el.name,
                  value: el.id,
                }) as SelectItem
            )
        )
      );
  }

  getSeaLoadPoints(stationId: string): Observable<SelectItem[]> {
    return this.getQuery()
      .getLoadpoints()
      .pipe(
        map((loadpoints) =>
          loadpoints
            .filter((el) => el.active && el.stationId === stationId && el.sea)
            .map(
              (el) =>
                ({
                  label: el.name,
                  value: el.id,
                }) as SelectItem
            )
        )
      );
  }

  getActiveStation(): Observable<StationModel> {
    return this.query.getActiveStation();
  }

  setActiveStation(station: StationModel) {
    this.store.update((state) => {
      return {
        ...state,
        activeStation: station,
      };
    });
  }

  setDisableSaveBtn(value: boolean) {
    this.store.update((state) => ({ ...state, disableSaveBtn: value }));
  }

  getDisableSaveBtn(): Observable<boolean> {
    return this.query.select('disableSaveBtn');
  }

  setLoadingSaveBtn(value: boolean) {
    this.store.update((state) => ({ ...state, loadingSaveButton: value }));
  }

  getLoadingSaveBtn(): Observable<boolean> {
    return this.query.select('loadingSaveButton');
  }

  getOperator(id: string): Observable<OperatorModel> {
    return this.operatorService.adminGetOperator(id).pipe(
      map(
        (res) =>
          ({
            id: res.id,
            name: res.name,
            companyId: res.companyId,
            type: res.type,
          }) as OperatorInfoModel
      )
    );
  }

  fetchStationHistory(id: string): Promise<ChangeEntryModel[]> {
    return this.changesService
      .getChanges('Station', id)
      .pipe(
        tap(async (historyLogs) => {
          this.store.update((state) => {
            return {
              ...state,
              historyLogs,
            };
          });
        })
      )
      .toPromise();
  }

  setLimit(limit: number) {
    this.store.update((state) => ({ ...state, limit }));
  }

  getNavigation(direction?: number, currentId?: string): Observable<StationModel> {
    return this.query.sortedStations$().pipe(
      take(1),
      map((items) => {
        if (!items.length) return null;
        const currentIndex = currentId ? items.findIndex((item) => item.id === currentId) : -1;
        let newIndex = currentIndex;

        if (typeof direction === 'number') {
          newIndex = currentIndex + direction;
          if (newIndex < 0 || newIndex >= items.length) {
            newIndex = 0;
          }
        } else if (currentIndex === -1) {
          newIndex = 0;
        }

        this.store.update({
          disbledPrevBtn: newIndex <= 0,
          disbledNextBtn: newIndex >= items.length - 1,
        });

        return items[newIndex] ?? null;
      })
    );
  }
}
